/**
 * Event Operations service — the operator surface's data layer (SPEC steps 3–6).
 * Thin wrappers over the event-scoped, can_operate_event_reward-gated RPCs (migrations
 * 2026-07-25-001/003/005/010/012/013). Every call is authorized server-side; a 42501 means the caller
 * isn't an operator of that event.
 */
import { supabase } from "./supabase";

export interface OperationsRosterRow {
  attendee_id: string;
  contact_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  attendance_status: string | null;
  checked_in_at: string | null;
  attendee_type: string | null;
  invited_by_name: string | null;
  is_member_reward_eligible: boolean;
  reward_state: string | null;
  assigned_offer_name: string | null;
  /** Per-attendee correct award to (re-)issue — resolved server-side from the attendee's own ticket. */
  suggested_offer_id: string | null;
  suggested_offer_name: string | null;
  is_overridable: boolean;
  overridable_reason: string | null;
  /** True only when overridable AND the reward isn't already the suggested award. Drive Fix-reward off this. */
  needs_reward_fix: boolean;
  /** Whether the event can host referrals (has a conversion engine). Event-level; same on every row. */
  referrals_available: boolean;
  /** Current reward dollar value on the voucher (base + any +$20 ratchets). */
  reward_value: number | null;
  /** How many +$20 MSR ratchets have been applied (0–2). */
  ratchet_count: number;
  /** Ratchets still allowed (2 − ratchet_count). */
  ratchets_remaining: number;
  /** The member's subscribed product(s) from Zuora — for the MSR to interpret a tier change. */
  subscribed_products: string[] | null;
  /** Live (non-void) ticket count for this attendee. >1 ⇒ ambiguous (is_overridable false). */
  live_token_count: number;
  /** Server-authoritative gate for the +$20 button — matches every precondition the ratchet RPC
   *  enforces (overridable + single live ticket + CHECKED IN + monetary reward + under the cap). */
  can_ratchet: boolean;
  /** The member's subscription start date (contacts.member_start_date, date-only ISO), or null.
   *  Present whenever we have a Zuora membership start on file. */
  member_start_date: string | null;
  /** True when this registrant is an active member AND their membership started on/after they
   *  registered for THIS event — i.e. they converted to a member at/around the event. */
  is_new_member: boolean;
  /** Server-authoritative: a reward voucher exists on this attendee's live ticket. */
  has_reward_voucher: boolean;
  /** Server-authoritative gate for the operator "+$20": an existing overridable monetary reward under
   *  the cap, OR a from-scratch bootstrap (checked-in, linked contact, ≤1 ticket, no voucher yet). */
  can_start_or_ratchet: boolean;
  /** Companion seam 3: this attendee bought a membership via THIS event's CTA (a 'New Subscription'
   *  voucher scoped to this event exists). Drives the "Bought membership" badge + inviter-credit prompt. */
  membership_purchased: boolean;
  /** When that membership purchase landed (earliest matching voucher), ISO, or null. */
  membership_purchased_at: string | null;
  /** Companion seam 4: the inviting member's OWN checked-in attendee row in this event, to ratchet as a
   *  credit. Null when the inviter isn't checked in (nothing to credit yet). Only meaningful on a
   *  companion row (invited_by_name set). */
  inviter_attendee_id: string | null;
  /** Companion seam 4: a referral credit for THIS companion already exists (server-side per-companion
   *  idempotency). The Operations tab shows "Credited ✓" instead of re-offering the credit button. */
  referral_credited: boolean;
  /** Where this person sits on their Zuora membership. TRI-STATE, and the null matters:
   *  true = they are the primary; false = they are a secondary on someone else's membership;
   *  null = they are not tied to a Zuora account, so we do not know.
   *  Never coerce null to false. `contacts.is_primary_member` DEFAULTs to true, so every prospect
   *  and kiosk-created guest reads "primary" at the column level — the server only emits a value
   *  when a Zuora account is actually present (451 of 2,366 linked PROD attendees would otherwise
   *  be badged "Primary" while holding no membership at all). */
  is_primary_member: boolean | null;
}

/** Bounded whole-event roster with reward state + per-attendee fix target (get_event_operations_roster). */
export async function getEventOperationsRoster(
  eventId: string,
): Promise<OperationsRosterRow[]> {
  const { data, error } = await supabase.rpc("get_event_operations_roster", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as OperationsRosterRow[];
}

/**
 * Export the event's CHECKED-IN roster as a CSV, with account/contact-level appends (member number,
 * product/subscription, home address, member status) resolved at runtime from the linked contact.
 * Server-gated by can_operate_event_reward (same operator gate as the rest of this tab), so a 42501
 * means the caller isn't an operator of this event. Builds the whole file in one snapshot server-side
 * (export_event_checkin_roster_csv) and triggers a browser download. Returns the row count.
 */
export async function downloadEventCheckinRosterCsv(
  eventId: string,
  filenameHint?: string | null,
): Promise<number> {
  const { data, error } = await supabase.rpc(
    "export_event_checkin_roster_csv",
    { p_event_id: eventId },
  );
  if (error) {
    throw error;
  }
  const row = (Array.isArray(data) ? data[0] : data) as
    | { csv?: string; row_count?: number }
    | undefined;
  const csv = row?.csv ?? "";
  const slug = (filenameHint ?? "event")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  const stamp = new Date().toISOString().slice(0, 10);
  const filename = `checkin-roster_${slug || "event"}_${stamp}.csv`;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  return Number(row?.row_count ?? 0);
}

export interface FixRewardResult {
  ok: boolean;
  tier: string;
  reward_state: string;
  assigned_offer_name: string | null;
  assigned_value: number | null;
}

/**
 * "Fix reward" (fix_event_attendee_reward): re-issue the CURRENT-correct award for an attendee. Takes
 * NO offer — the server derives the tier from the contact's current eligibility under lock, so a stale
 * roster can never freeze the wrong tier. p_command_id is a fresh per-click UUID (idempotency).
 * (The lower-level explicit-offer override assign_event_attendee_reward still exists server-side but is
 * intentionally NOT used for the auto-fix, to avoid passing a client-chosen, possibly-stale tier.)
 */
export async function fixEventAttendeeReward(params: {
  eventId: string;
  attendeeId: string;
  commandId: string;
}): Promise<FixRewardResult> {
  const { data, error } = await supabase.rpc("fix_event_attendee_reward", {
    p_event_id: params.eventId,
    p_attendee_id: params.attendeeId,
    p_command_id: params.commandId,
  });
  if (error) {
    throw error;
  }
  return data as FixRewardResult;
}

export interface RatchetResult {
  ok: boolean;
  reward_value: number;
  ratchets: number;
  ratchets_remaining: number;
}

/**
 * "+$20" ratchet (ratchet_event_attendee_reward): the MSR adds a fixed $20 to a checked-in attendee's
 * ticket reward — activity-based (membership tier change / guest referral), never freeform, capped at 2
 * (max +$40). Independent of auto-escalation; refuses a redeemed/cancelled reward. p_command_id is a
 * fresh per-tap UUID (idempotency — a double-tap doesn't double-add).
 */
export async function ratchetEventAttendeeReward(params: {
  eventId: string;
  attendeeId: string;
  commandId: string;
}): Promise<RatchetResult> {
  const { data, error } = await supabase.rpc("ratchet_event_attendee_reward", {
    p_event_id: params.eventId,
    p_attendee_id: params.attendeeId,
    p_command_id: params.commandId,
  });
  if (error) {
    throw error;
  }
  return data as RatchetResult;
}

/**
 * Result of the event-attendee-reward edge function (ratchet / resend_qr). Ratchet bootstraps the
 * ratchet's preconditions (token + $0 base voucher) for ANY checked-in registrant, then delegates to
 * the UNTOUCHED ratchet_event_attendee_reward RPC. resend_qr (re)emails the attendee's QR ticket.
 */
export interface AttendeeRewardResult {
  ok?: boolean;
  reward_value?: number;
  ratchets?: number;
  ratchets_remaining?: number;
  /** Referral-credit results (credit_referral action): count of referral credits this member has for the
   *  event, and how many remain under the separate referral cap. */
  referral_credits?: number;
  referral_remaining?: number;
  token_code?: string | null;
  idempotent_replay?: boolean;
  emailed?: boolean;
  emailReason?: string;
  ticketCode?: string | null;
  qrUrl?: string | null;
  reason?: string;
  error?: string;
}

async function invokeAttendeeReward(
  body: Record<string, unknown>,
): Promise<AttendeeRewardResult> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  const { data, error } = await supabase.functions.invoke(
    "event-attendee-reward",
    {
      body,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    },
  );
  if (error) {
    // Surface the edge function's own {error} message (a non-2xx sets `error` with a generic message;
    // the real reason is in the JSON body on error.context).
    let msg = error.message;
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === "function") {
        const b = await ctx.json();
        if (b?.error) {
          msg = b.error as string;
        }
      }
    } catch {
      /* fall back to error.message */
    }
    throw new Error(msg);
  }
  return (data ?? {}) as AttendeeRewardResult;
}

/**
 * Ratchet ANY checked-in registrant by +$20 (cap $40). Bootstraps a token + $0 base voucher first if
 * the registrant has none (event never reward-provisioned), then the untouched ratchet applies +$20.
 * Optionally (re)emails the QR ticket that now redeems for the new value.
 */
export async function ratchetOrStartAttendeeReward(params: {
  eventId: string;
  attendeeId: string;
  commandId: string;
  sendQr?: boolean;
}): Promise<AttendeeRewardResult> {
  return invokeAttendeeReward({
    action: "ratchet",
    eventId: params.eventId,
    attendeeId: params.attendeeId,
    commandId: params.commandId,
    sendQr: params.sendQr === true,
  });
}

/**
 * Credit the inviting member for bringing a companion who bought a membership at the event (+$20, from a
 * SEPARATE referral budget capped at $40/member/event — it does NOT consume the member's own +$20 ratchet
 * budget). Bootstraps the inviter's base reward, then adds value under the distinct 'referral_credit'
 * source. The server re-validates the companion↔inviter link, the event-scoped purchase, active-member
 * inviter, per-companion idempotency (a companion credits at most once), and the separate cap.
 */
export async function creditReferralReward(params: {
  eventId: string;
  inviterAttendeeId: string;
  companionAttendeeId: string;
  commandId: string;
}): Promise<AttendeeRewardResult> {
  return invokeAttendeeReward({
    action: "credit_referral",
    eventId: params.eventId,
    attendeeId: params.inviterAttendeeId,
    companionAttendeeId: params.companionAttendeeId,
    commandId: params.commandId,
  });
}

/** (Re)send the attendee's QR ticket by email — never got it / misplaced it. Mints a token if needed. */
export async function resendAttendeeQr(params: {
  eventId: string;
  attendeeId: string;
}): Promise<AttendeeRewardResult> {
  return invokeAttendeeReward({
    action: "resend_qr",
    eventId: params.eventId,
    attendeeId: params.attendeeId,
  });
}

/**
 * Get the attendee's QR ticket image URL for ON-SCREEN display (operator shows it so the attendee can
 * photograph it). Materializes the token + QR on demand (mints/generates if missing); does NOT email.
 */
export async function showAttendeeQr(params: {
  eventId: string;
  attendeeId: string;
}): Promise<AttendeeRewardResult> {
  return invokeAttendeeReward({
    action: "show_qr",
    eventId: params.eventId,
    attendeeId: params.attendeeId,
  });
}

export interface EventReferralLink {
  ok: boolean;
  referral_token: string | null;
  /** The shareable /refer path. The redeemable voucher code is intentionally NOT returned. */
  referral_path: string | null;
}

/** Event-scoped referral link for an attendee (create_event_referral_link, bridge A). */
export async function createEventReferralLink(
  eventId: string,
  attendeeId: string,
): Promise<EventReferralLink> {
  const { data, error } = await supabase.rpc("create_event_referral_link", {
    p_event_id: eventId,
    p_attendee_id: attendeeId,
  });
  if (error) {
    throw error;
  }
  return data as EventReferralLink;
}
